// @flow

import Promise from 'bluebird';

import { convertToApiQuery } from '@seek/seek-jobs-search-query';
import { isMobileUserAgent } from 'src/modules/chalice-user-agent/device-detect';

import {
  withRequestId,
  withUserAgent,
  createUnauthenticatedHttpClient
} from 'src/modules/seek-api-request';
import { get, has } from 'lodash';
import clean from 'src/modules/clean-object';

import {
  searchUrl as jobsearchUrl,
  countsUrl,
  relatedSearchesUrl
} from 'src/config';

// Matches the jobsearch api timeout. Speak to the #jobsearch
// team when changing this value.
export const SEARCH_API_TIMEOUT = 3000;

let onlySearchPromise, onlyCountsPromise, onlyRelatedSearchPromise;

function buildApiQuery(searchQuery, locale = 'AU', cookies, userQueryId) {
  const analyticsParams = !cookies
    ? {}
    : clean({
        userqueryid: userQueryId || null,
        userid: cookies.JobseekerVisitorId || null,
        usersessionid: cookies.JobseekerVisitorId || null,
        eventCaptureSessionId: cookies.JobseekerSessionId || null
      });

  const searchParams = convertToApiQuery({ locale, searchQuery });

  return {
    siteKey: `${locale.toUpperCase()}-Main`,
    sourcesystem: 'houston',
    ...analyticsParams,
    ...searchParams,
    isDesktop: !isMobileUserAgent()
  };
}

function buildApiHeaders(apiQuery, requestId) {
  // Tell the jobsearch api not to talk to the scala api
  // Soon @prest will make the companyname search behave in a way
  // that doesn't require this flag, and we can remove this function

  const overrideFeaturesOff = apiQuery.companyname
    ? {
        'X-Seek-Override-Features-Off': '0b4acc6d-4f36-4ecd-92c6-eec5870ff9bb'
      }
    : {};

  return {
    ...overrideFeaturesOff,
    ...withRequestId(requestId)
  };
}

function search({
  searchParams,
  locale,
  cookies,
  userQueryId,
  requestId,
  timeout = SEARCH_API_TIMEOUT,
  userAgent
}: {
  searchParams: SearchParams,
  locale: Locale,
  cookies: Object,
  userQueryId: string,
  requestId: ?string,
  timeout: number,
  userAgent: string
}) {
  const searchUrl = jobsearchUrl;

  if (onlySearchPromise) {
    onlySearchPromise.cancel();
  }
  const apiQuery = buildApiQuery(searchParams, locale, cookies, userQueryId);

  const searchPromise = Promise.resolve(
    createUnauthenticatedHttpClient({ retryPolicy: { retries: 0 } }).send({
      url: searchUrl,
      timeout,
      params: apiQuery,
      label: 'search-experience-api',
      headers: {
        ...buildApiHeaders(apiQuery, requestId),
        ...withUserAgent(userAgent)
      }
    })
  ).then(response => {
    const result = get(response, 'data');

    if (has(result, 'data')) {
      return result;
    }

    throw new Error(`invalid api response: ${JSON.stringify(result)}`);
  });

  if (ENV.CLIENT) {
    onlySearchPromise = searchPromise;
  }

  return searchPromise;
}

function counts({
  searchParams,
  locale,
  cookies,
  requestId,
  timeout = SEARCH_API_TIMEOUT
}: {
  searchParams: SearchParams,
  locale: Locale,
  cookies: Object,
  requestId: ?string,
  timeout?: number
}) {
  if (onlyCountsPromise) {
    onlyCountsPromise.cancel();
  }

  const countsPromise = Promise.resolve(
    createUnauthenticatedHttpClient({ omitXSeekSiteHeader: true }).send({
      url: countsUrl,
      timeout,
      label: 'search-counts',
      params: buildApiQuery(searchParams, locale),
      headers: buildApiHeaders(cookies, requestId)
    })
  ).then(response => response.data);

  if (ENV.CLIENT) {
    onlyCountsPromise = countsPromise;
  }

  return countsPromise;
}

function relatedSearches({
  country,
  keywords,
  requestId,
  timeout
}: {
  country: 'au' | 'nz',
  keywords: string,
  requestId: ?string,
  timeout: number
}): Promise<*> {
  if (onlyRelatedSearchPromise) {
    onlyRelatedSearchPromise.cancel();
  }

  const relatedSearchPromise = Promise.resolve(
    createUnauthenticatedHttpClient({ omitXSeekSiteHeader: true }).send({
      url: relatedSearchesUrl,
      timeout,
      label: 'related-search',
      params: { country, keywords },
      headers: withRequestId(requestId)
    })
  ).then(({ data }) => data);

  if (ENV.CLIENT) {
    onlyRelatedSearchPromise = relatedSearchPromise;
  }

  return relatedSearchPromise;
}

export default {
  search,
  counts,
  relatedSearches
};
